import React from 'react';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import format from 'date-fns/format';
import isPast from 'date-fns/isPast';
import isToday from 'date-fns/isToday';

const WaitingPaymentsBreakdown = ({ payments = [], onClose }) => {
  const now = new Date();
  
  const waitingPayments = payments
    .filter(p => p.status === 'Waiting for payment')
    .map(p => ({
      ...p,
      isOverdue: isPast(new Date(p.date)) && !isToday(new Date(p.date))
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  const overduePayments = waitingPayments.filter(p => p.isOverdue);
  const pendingPayments = waitingPayments.filter(p => !p.isOverdue);
  
  if (waitingPayments.length === 0) {
    return (
      <Card sx={{ mt: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Payment Schedule</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography color="textSecondary" sx={{ mt: 1 }}>No pending payments</Typography>
        </CardContent>
      </Card>
    );
  }

  const totalWaiting = waitingPayments.reduce((sum, p) => sum + p.total, 0);
  const totalOverdue = overduePayments.reduce((sum, p) => sum + p.total, 0);
  const totalPending = pendingPayments.reduce((sum, p) => sum + p.total, 0);

  return (
    <Card sx={{ mt: 2, mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" component="span">Payment Schedule</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {pendingPayments.length > 0 && (
                <Chip 
                  label={`${pendingPayments.length} pending · $${totalPending.toLocaleString()}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {overduePayments.length > 0 && (
                <Chip 
                  label={`${overduePayments.length} overdue · $${totalOverdue.toLocaleString()}`}
                  size="small"
                  color="error"
                  variant="filled"
                />
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <TableContainer sx={{ maxHeight: 450 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Loan</TableCell>
                <TableCell align="right">Total to Return</TableCell>
                <TableCell align="right">Already Paid</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="center">Payments</TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>Completion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waitingPayments.map((payment) => {
                const total_to_return = payment.loan_total_to_return || 0;
                const already_paid = payment.already_paid || 0;
                const remaining = total_to_return - already_paid;
                const completionPercent = total_to_return > 0
                  ? ((already_paid / total_to_return) * 100)
                  : 0;
                
                return (
                  <TableRow 
                    key={payment.id} 
                    hover
                    sx={payment.isOverdue ? { backgroundColor: 'rgba(211, 47, 47, 0.08)' } : {}}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {format(new Date(payment.date), 'MMM dd, yyyy')}
                        {payment.isOverdue && (
                          <Chip label="overdue" size="small" color="error" sx={{ height: 20, fontSize: '10px' }} />
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell align="right">
                      <span style={{ 
                        color: payment.isOverdue ? '#d32f2f' : 'inherit', 
                        fontWeight: payment.isOverdue ? 'bold' : 'normal' 
                      }}>
                        ${payment.total.toLocaleString()}
                      </span>
                    </TableCell>
                    
                    <TableCell align="right">
                      {payment.loan_id ? (
                        <span
                          onClick={() => window.open(`/loans/${payment.loan_id}/show`, '_blank')}
                          style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          #{payment.loan_id} · ${(payment.loan_amount || 0).toLocaleString()}
                        </span>
                      ) : (
                        <span>${(payment.loan_amount || 0).toLocaleString()}</span>
                      )}
                    </TableCell>
                    
                    <TableCell align="right">
                      <span style={{ fontWeight: 500 }}>
                        ${total_to_return.toLocaleString()}
                      </span>
                    </TableCell>
                    
                    <TableCell align="right">
                      <span style={{ color: '#2e7d32' }}>
                        ${already_paid.toLocaleString()}
                      </span>
                    </TableCell>
                    
                    <TableCell align="right">
                      <span style={{ color: '#ed6c02', fontWeight: 500 }}>
                        ${remaining.toLocaleString()}
                      </span>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {payment.paid_count || 0} / {payment.loan_number_payments || 0}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 60 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(completionPercent, 100)} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 5,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#1976d2'
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 40, fontWeight: 500 }}>
                          {completionPercent.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default WaitingPaymentsBreakdown;